import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, CalendarDays, RotateCcw, Search } from "lucide-react";
import { CoverageBrand, ErrorMessage } from "./CoverageShared";
import { coverageFetch } from "./coverageApi";
import { attachCoverageFrame } from "./embedProtocol";

export default function CoverageDemo({ demoId }) {
  const [partner, setPartner] = useState(null);
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState("Open coverage");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const frame = useRef(null);
  useEffect(() => {
    const c = new AbortController();
    coverageFetch(`/partner-coverage/demos/${encodeURIComponent(demoId)}`, {
      signal: c.signal,
    })
      .then(setPartner)
      .catch((e) => {
        if (e.name !== "AbortError") setError(e.message);
      });
    return () => c.abort();
  }, [demoId]);
  useEffect(() => {
    if (!frame.current || !session) return;
    return attachCoverageFrame(frame.current, session, (event) => {
      if (event.type === "error") {
        setError(event.message);
        setLoading(false);
      }
      if (event.type === "loaded") setLoading(false);
      if (event.type === "selection.changed")
        setStatus(`${event.count} saved on RxRooster`);
    });
  }, [session]);
  const start = async () => {
    setOpen(true);
    setError("");
    setLoading(true);
    setStatus("Open coverage");
    try {
      setSession(
        await coverageFetch(
          `/partner-coverage/v3/demos/${encodeURIComponent(demoId)}/session`,
          { method: "POST" },
        ),
      );
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };
  return (
    <main
      className="cvg cvg-demo"
      style={{ "--cvg-accent": partner?.accent || "#167d8d" }}
    >
      <div className="cvg-demo-banner">
        INTERACTIVE DEMO · Fictional providers and facility · No messages or
        schedule changes
      </div>
      <header className="cvg-top">
        <CoverageBrand />
        <span className="cvg-pill">
          {partner?.name || "Your system"} + RxRooster
        </span>
      </header>
      <div className="cvg-demo-body">
        <div className="cvg-heading">
          <div>
            <span className="cvg-eyebrow">A LOOK INSIDE YOUR WORKFLOW</span>
            <h1>
              Your schedule.
              <br />
              <span>More ways to find coverage.</span>
            </h1>
            <p>
              Open a vacancy. Explore matching providers on RxRooster.
              <br />
              Build a shortlist without leaving your system.
            </p>
          </div>
          <div className="cvg-demo-path">
            <span>01 Open vacancy</span>
            <ArrowRight size={18} />
            <span>02 Browse providers</span>
            <ArrowRight size={18} />
            <span>03 Connect on RxRooster</span>
          </div>
        </div>
        <ErrorMessage>{error}</ErrorMessage>
        <div className={`cvg-demo-layout ${open ? "cvg-demo-open" : ""}`}>
          <section className="cvg-demo-board">
            <header>
              <div>
                <span className="cvg-eyebrow">
                  {partner?.name || "SCHEDULING SYSTEM"}
                </span>
                <h2>Operating room board</h2>
              </div>
              <CalendarDays size={24} />
            </header>
            <div className="cvg-gap-banner">
              <div>
                <b>1 CRNA coverage gap</b>
                <p>Demo Medical Center · OR 03 · 07:00–15:00</p>
              </div>
              <button
                className="cvg-button"
                onClick={start}
                disabled={!partner || loading}
              >
                <Search size={16} />
                {open ? "Restart demo" : "Find coverage"}
              </button>
            </div>
            <div className="cvg-board-time">
              <span>ROOM</span>
              <span>07:00</span>
              <span>11:00</span>
              <span>15:00</span>
            </div>
            {[1, 2, 3, 4].map((room) => (
              <div className="cvg-room" key={room}>
                <b>OR 0{room}</b>
                {room === 3 ? (
                  <button
                    className="cvg-open-gap"
                    onClick={start}
                    disabled={!partner || loading}
                  >
                    <b>{status}</b>
                    <span>
                      CRNA · Find provider services <ArrowRight size={15} />
                    </span>
                  </button>
                ) : (
                  <div className="cvg-staffed">
                    <b>Assigned · CRNA</b>
                    <span>
                      {room === 2 ? "Orthopedics" : "General anesthesia"}
                    </span>
                  </div>
                )}
              </div>
            ))}
            <p className="cvg-muted">
              An illustrative board for {partner?.name || "your system"}. Your
              implementation keeps your own scheduling interface.
            </p>
            <div className="cvg-note cvg-demo-explainer">
              <h2>The next step is a conversation.</h2>
              <p>
                Providers publish their terms. Your team reviews the fit and
                expresses interest. Continue in the existing RxRooster Pipeline
                for the negotiation.
              </p>
              <p>
                Coverage is confirmed together after dates, terms and facility
                approval are agreed.
              </p>
            </div>
          </section>
          {open && (
            <section className="cvg-demo-frame">
              <div className="cvg-section-title">
                <span className="cvg-eyebrow">EMBEDDED RXROOSTER SEARCH</span>
                <button
                  className="cvg-icon-button"
                  aria-label="Restart coverage demo"
                  onClick={start}
                >
                  <RotateCcw size={16} />
                </button>
              </div>
              {loading && <p role="status">Opening your coverage panel…</p>}
              <iframe
                ref={frame}
                style={{
                  height: "min(680px, calc(100dvh - 100px))",
                  minHeight: 400,
                }}
                title="RxRooster provider coverage search demo"
                referrerPolicy="no-referrer"
              />
            </section>
          )}
        </div>
      </div>
    </main>
  );
}
